import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "lt-pwa-install-dismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * Banner de instalação do PWA.
 * - Android/Chromium: usa o evento beforeinstallprompt nativo.
 * - iOS Safari: mostra instruções "Compartilhar → Adicionar à Tela de Início".
 */
export const PwaInstallPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch {
      /* ignore */
    }

    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    if (isIos()) {
      const t = setTimeout(() => {
        setShowIos(true);
        setVisible(true);
      }, 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBip);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-lt-border bg-background/95 p-4 shadow-2xl backdrop-blur">
        <div className="flex items-start gap-3">
          <img src="/pwa-192.png" alt="" width={40} height={40} className="rounded-lg" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-foreground">Instalar LotoTerminal</p>
            {showIos ? (
              <p className="mt-1 text-xs text-lt-cream3 leading-relaxed">
                Para instalar no iPhone, toque em{" "}
                <Share className="inline h-3.5 w-3.5 align-text-bottom" /> Compartilhar e depois em{" "}
                <span className="whitespace-nowrap">
                  <Plus className="inline h-3.5 w-3.5 align-text-bottom" /> "Adicionar à Tela de Início"
                </span>
                .
              </p>
            ) : (
              <p className="mt-1 text-xs text-lt-cream3">
                Instale o app no seu celular para acesso rápido e modo standalone.
              </p>
            )}
            {deferred && (
              <button
                onClick={install}
                className="mt-2 rounded-md bg-lt-gold px-3 py-1.5 text-xs font-medium text-lt-dark hover:opacity-90"
              >
                Instalar agora
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            aria-label="Fechar"
            className="rounded-md p-1 text-lt-cream3 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;